import { useLang } from 'hooks/lang';

import { actions, User, taskStatusByCode, isLeader, TaskWithSub } from 'entities';

import { useToast } from 'providers/Toast';
import { useUser } from 'hooks/user';
import { useSingleOwnerChange } from 'providers/OwnershipChange';

type OwnerType = 'owner' | 'subOwner';

interface TaskOwnershipChangeParams<O extends OwnerType> {
  task: TaskWithSub;
  type: O;
  onNewOwner?: O extends 'owner' ? (owner: User) => void : (owner?: User) => void;
}

export function useTaskOwnerChange<O extends OwnerType>({
  task,
  type,
  onNewOwner,
}: TaskOwnershipChangeParams<O>) {
  const { t } = useLang();
  const { addToast } = useToast();
  const user = useUser();

  const isOwnerChange = type === 'owner';

  const canChangeOwner = [
    user.team.id === task.owner.team,
    isLeader(user),
    taskStatusByCode[task.status][isOwnerChange ? 'canChangeOwner' : 'canChangeSubOwner'],
  ].every(Boolean);

  const changeOwnership = actions.projects.changeTaskOwnership.use();

  const handleChangeClick = useSingleOwnerChange({
    text: `${t(isOwnerChange ? 'change-task-owner' : 'change-task-sub-owner')}?`,

    currentOwner: isOwnerChange ? task.owner : (task.subOwner as User),

    cancel: () => addToast({ title: t('task-ownership-change-aborted') }),

    allowRemoval: !isOwnerChange,

    onConfirm: async (owner, justification) => {
      const commonParams = { project: task.projectId, task: task.id, justification };

      await (isOwnerChange
        ? changeOwnership.execute({
            ...commonParams,
            owner: owner!.id,
          })
        : changeOwnership.execute({
            ...commonParams,
            subOwner: owner?.id ?? null,
          }));

      onNewOwner?.(owner!);
    },
  });

  return {
    handleChangeClick,
    canChangeOwner,
    loading: changeOwnership.loading,
  };
}
